import { type ISelectValue } from "components/Inputs/Select";

import { enumToValuesArray, getEnumValue } from "api/common/enumHelper";
import {
    FilterDateEqualsEnum,
    FilterNumberEqualsEnum,
    FilterTextEqualsEnum,
} from "api/common/filters";
import { ContactTypeEnum } from "api/enums/ContactTypeEnum";
import { EquipmentStatusEnum } from "api/enums/Equipment/EquipmentStatusEnum";
import { MaintenanceTypeEnum } from "api/enums/Equipment/MaintenanceTypeEnum";
import { ExternalResourceStatusEnum } from "api/enums/ExternalResources/ExternalResourceStatusEnum";
import { OkkComplaintStatusEnum } from "api/enums/OkkComplaints/OkkComplaintStatusEnum";
import { PermissionsEnum } from "api/enums/PermissionsEnum";
import { SdClaimStatusEnum } from "api/enums/Sd/SdClaimStatusEnum";
import { TradePointBrandEnum } from "api/enums/TradePoints/TradePointBrandEnum";
import { TradePointFiscalDataTypeEnum } from "api/enums/TradePoints/TradePointFiscalDataTypeEnum";
import { TradePointFormatEnum } from "api/enums/TradePoints/TradePointFormatEnum";
import { TradePointOwnerTypeEnum } from "api/enums/TradePoints/TradePointOwnerTypeEnum";
import { UserRoleEnum } from "api/enums/Users/UserRoleEnum";
import { UserStatusEnum } from "api/enums/Users/UserStatusEnum";
import { WorkManagerEventStatusEnum } from "api/enums/WorkManager/WorkManagerEventStatusEnum";
import { type ISortData, SortOrderEnum } from "api/interfaces/components/GoodTable";

import { GptModelsEnum } from "api/enums/GptModelEnum";
import { SurveyQuestionTypeEnum } from "api/enums/SurveyQuestionTypeEnum";
import { SurveyStatusFlag } from "api/enums/SurveyStatusFlag";

import { StatisticFilterCompareTypeEnum } from "api/enums/Statistics/StatisticFilterCompareTypeEnum";
import { StatisticPeriodEnum } from "api/enums/Statistics/StatisticPeriodEnum";

import { StatisticTypeEnum } from "api/enums/Statistics/StatisticTypeEnum";

import { getComplexTableFieldValue } from "api/common/table";

import { ReportListEnum } from "api/enums/Reports/ReportListEnum";

import { ThirdPartyReportShowCategoryEnum } from "api/enums/ThirdPartyReportShowCategoryEnum";

import { UserSexEnum } from "api/enums/Users/UserSexEnum";

import { WorkManagerPayrollStatusEnum } from "api/enums/WorkManager/WorkManagerPayrollStatusEnum";

import ruLang from "./ru";

type TLangTypes = "ru";
const shortName: TLangTypes = "ru";
const name = "ru-RU";

interface ILangPlural {
    [key: string]: string[];
}
interface ILangPipes {
    [key: string]: { [key2: string]: string };
}

const lang = ruLang;

/**
 * первый символ в нижний регистр
 * @param value исходная строка
 * @returns строка с первым символом в нижний регистр
 */
function getStringWithFirstLetterLowercase(value: string) {
    return value.charAt(0).toLocaleLowerCase() + value.slice(1);
}

export function getLangValue(langObject: any, field: string | number, defValue: string = "") {
    if (field in langObject) {
        return langObject[field];
    }
    return defValue;
}

export function getLocaleName(type = "") {
    switch (type) {
        case "short":
            return shortName;
        default:
            return name;
    }
}

/**
 * Подставляет в строку плейсхолдеры вида %p#key# по правилам множественного числа для ru-RU.
 */
export function plural(count: number, text: string, withCount = true) {
    /** Правила множественного числа для текущей локали (замена plural-ru) */
    const pluralRules = new Intl.PluralRules(name);

    /**
     * Выбирает форму слова для русского языка по числу (one / few / many).
     */
    const selectPluralFormRu = (count: number, one: string, few: string, many: string): string => {
        switch (pluralRules.select(count)) {
            case "one":
                return one;
            case "few":
                return few;
            default:
                return many;
        }
    };
    const startPos = text.indexOf("%p#");
    const endPos = text.indexOf("#", startPos + 3);
    const pluralKey = text.substring(startPos + 3, endPos);
    const langPlural = lang.plural as ILangPlural;

    if (langPlural?.[pluralKey]?.length) {
        const arr = langPlural[pluralKey];
        let pluralText = selectPluralFormRu(count, arr[0], arr[1], arr[2]);
        if (withCount) {
            pluralText = `${count} ${pluralText}`;
        }
        return text.replace("%p#" + pluralKey + "#", pluralText);
    }
    return text;
}
export function sprintf(text: string, ...values: (string | number)[]): string {
    if (typeof text == "string" && values?.length) {
        for (const value of values) {
            text = text.replace("%s", value.toString());
        }
    }
    return text;
}
export function getEnumTitleValue(
    enumObject: unknown,
    enumName: string,
    enumValue: number,
    defaultTitle: string = ""
): string {
    const value = getEnumValue(enumObject, enumValue);
    if (value !== null) {
        return getLangPipeValue(enumName, value);
    }
    return defaultTitle;
}
export type TEnumName =
    | "UserRoleEnum"
    | "UserStatusEnum"
    | "UserSexEnum"
    | "TradePointBrandEnum"
    | "TradePointFormatEnum"
    | "TradePointOwnerTypeEnum"
    | "OkkComplaintStatusEnum"
    | "ExternalResourceStatusEnum"
    | "PermissionsEnum"
    | "TradePointFiscalDataTypeEnum"
    | "SdClaimStatusEnum"
    | "WorkManagerEventStatusEnum"
    | "FilterDateEqualsEnum"
    | "FilterNumberEqualsEnum"
    | "FilterTextEqualsEnum"
    | "ContactTypeEnum"
    | "SurveyQuestionTypeEnum"
    | "SurveyStatusFlag"
    | "EquipmentStatusEnum"
    | "MaintenanceTypeEnum"
    | "GptModelsEnum"
    | "StatisticPeriodEnum"
    | "StatisticFilterCompareTypeEnum"
    | "StatisticTypeEnum"
    | "ReportListEnum"
    | "ThirdPartyReportShowCategoryEnum"
    | "WorkManagerPayrollStatusEnum";

/**
 * возвращает Enum по его имени
 */
export function getEnumByName(enumName: TEnumName): unknown {
    switch (enumName) {
        case "UserRoleEnum":
            return UserRoleEnum;
        case "UserStatusEnum":
            return UserStatusEnum;
        case "UserSexEnum":
            return UserSexEnum;
        case "TradePointBrandEnum":
            return TradePointBrandEnum;
        case "TradePointFormatEnum":
            return TradePointFormatEnum;
        case "TradePointOwnerTypeEnum":
            return TradePointOwnerTypeEnum;
        case "OkkComplaintStatusEnum":
            return OkkComplaintStatusEnum;
        case "ExternalResourceStatusEnum":
            return ExternalResourceStatusEnum;
        case "PermissionsEnum":
            return PermissionsEnum;
        case "TradePointFiscalDataTypeEnum":
            return TradePointFiscalDataTypeEnum;
        case "SdClaimStatusEnum":
            return SdClaimStatusEnum;
        case "WorkManagerEventStatusEnum":
            return WorkManagerEventStatusEnum;
        case "FilterDateEqualsEnum":
            return FilterDateEqualsEnum;
        case "FilterNumberEqualsEnum":
            return FilterNumberEqualsEnum;
        case "FilterTextEqualsEnum":
            return FilterTextEqualsEnum;
        case "ContactTypeEnum":
            return ContactTypeEnum;
        case "EquipmentStatusEnum":
            return EquipmentStatusEnum;
        case "MaintenanceTypeEnum":
            return MaintenanceTypeEnum;
        case "SurveyQuestionTypeEnum":
            return SurveyQuestionTypeEnum;
        case "SurveyStatusFlag":
            return SurveyStatusFlag;
        case "GptModelsEnum":
            return GptModelsEnum;
        case "StatisticPeriodEnum":
            return StatisticPeriodEnum;
        case "StatisticFilterCompareTypeEnum":
            return StatisticFilterCompareTypeEnum;
        case "StatisticTypeEnum":
            return StatisticTypeEnum;
        case "ReportListEnum":
            return ReportListEnum;
        case "ThirdPartyReportShowCategoryEnum":
            return ThirdPartyReportShowCategoryEnum;
        case "WorkManagerPayrollStatusEnum":
            return WorkManagerPayrollStatusEnum;
    }
    throw new Error("unknown enumName:" + enumName);
}
/**
 * возвращает массив из [Enum,"EnumName"]
 */
export function getEnumDataByName(enumName: TEnumName): [unknown, string] {
    const enumValue = getEnumByName(enumName);
    return [enumValue, enumName];
}

/**
 * Сокращенная конструкция для getEnumTitleValue
 * @param enumName предопределенный набор имен Enum-ом
 * @param value значение из Enum
 * @param defaultTitle если не найдено, то вывести это
 *
 */

export function getEnumTitle(
    enumName: TEnumName,
    value: number,
    defaultTitle: string = ""
): string {
    const enumValue = getEnumByName(enumName);
    return getEnumTitleValue(enumValue, enumName, value, defaultTitle);
}

/**
 * список ISelectValue по названию enum
 * @param enumName
 * @param sortParams
 * @returns
 */
export function getEnumSelect(enumName: TEnumName, sortParams?: ISortData): ISelectValue[] {
    const enumValue = getEnumByName(enumName);
    return getEnumSelectValues(enumValue, enumName, sortParams);
}

export function getEnumSelectValues(
    enums: any,
    enumName: string,
    sortParams?: ISortData
): ISelectValue[] {
    const enumValues = enumToValuesArray(enums);

    const result: ISelectValue[] = enumValues.map((val) => {
        const name = enums[val];
        return {
            id: val,
            title: getLangPipeValue(enumName, String(name)),
        };
    });

    if (sortParams) {
        const sortedResult = sortArray<any>(result, sortParams.sort);
        return sortParams.direction === SortOrderEnum.Ascending
            ? sortedResult
            : sortedResult.reverse();
    }

    return result;
}

/**
 *
 * @param arr сортировка массива по имени поля
 * @param fieldName имя поля
 * @param options
 * @returns
 */
export function sortArray<T>(
    arr: T[] | undefined,
    fieldName: keyof T,
    options: Intl.CollatorOptions | undefined = { numeric: true, usage: "sort" }
): T[] {
    const sorter = getSortArrayCollator(options);
    if (!arr?.length) {
        return [];
    }

    const result = [...arr].sort((a, b) => {
        let paramA = a[fieldName];
        let paramB = b[fieldName];
        const fieldNameStr = fieldName.toString();

        if (
            (paramA === null || paramA === undefined) &&
            (paramB === null || paramB === undefined) &&
            fieldNameStr.split(".").length > 1
        ) {
            paramA = getComplexTableFieldValue(fieldNameStr, a);
            paramB = getComplexTableFieldValue(fieldNameStr, b);
        }

        return sorter.compare(String(paramA), String(paramB));
    });
    return result;
}
export function getSortArrayCollator(
    options: Intl.CollatorOptions | undefined = { numeric: true }
) {
    return new Intl.Collator(name, options);
}
function getLangPipeValue(enumName: string, valueName: string) {
    const langPipes = lang.pipes as ILangPipes;
    enumName = enumName.charAt(0).toLocaleLowerCase() + enumName.slice(1);
    if (enumName.endsWith("Enum") || enumName.endsWith("Flag")) {
        enumName = enumName.slice(0, -4);
    }
    valueName = getStringWithFirstLetterLowercase(valueName);

    return langPipes?.[enumName]?.[valueName] || valueName;
}
export default lang;
